import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { ReconObservations } from '@/sections/asset/components/tabs/LLMReconObservations';
import {
  McpPrompt,
  McpResource,
  McpTool,
  useLLMRecon,
} from '@/sections/asset/hooks/drawer/useLLMRecon';
import { Asset, UniqueQueryKeys } from '@/types';
import { useMutation } from '@/utils/api';

/**
 * RunReconButton triggers a recon-only augustus run against the target webpage.
 * It posts a job with a non-matching probe pattern (`__recon_only__`), which the
 * backend treats as recon-only (runs reconnaissance, emits observations, no
 * attack probes). Config is string-only — model.Job.Config is map[string]string,
 * so a boolean there 400s (which is why this uses a direct POST, not
 * useRunCapability). Disabled until the target webpage is resolved.
 */
function RunReconButton({
  webpageKey,
  discoveredBy,
}: {
  webpageKey?: string;
  discoveredBy?: string;
}) {
  const axios = useAxios();
  const runRecon = useMutation<unknown, unknown, void>({
    defaultErrorMessage: 'Failed to start recon',
    mutationFn: async () => {
      const { data } = await axios({
        method: 'POST',
        url: '/job',
        data: {
          key: webpageKey,
          capabilities: ['augustus'],
          config: { probes: '__recon_only__' },
        },
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Recon started', {
        description: 'The MCP inventory refreshes when it completes.',
      });
      // The recon job runs asynchronously on the compute worker — the POST only
      // enqueues it. Refresh now and again over the next ~20s so the panel picks
      // up the observations once the results pipeline has written them, instead
      // of grabbing a stale empty result immediately. invalidateQueries is a
      // no-op if the panel has unmounted, so the timers need no cleanup.
      const refresh = () => {
        // The panel discovers recon files by listing the observation convention
        // prefix (useMy resource:'file'); invalidate that so a completed rerun's
        // new files are picked up.
        queryClient.invalidateQueries({
          queryKey: getQueryKey.getMy('file'),
        });
        // The recon files (observations, recon-errors) live at stable paths, so
        // useGetFile — keyed only on the path — would keep serving the pre-rerun
        // content to an open drawer. Invalidate the whole getFile family so the
        // overwritten files refetch.
        queryClient.invalidateQueries({
          queryKey: [UniqueQueryKeys.GET_FILE],
        });
      };
      refresh();
      [4000, 10000, 18000].forEach(ms => setTimeout(refresh, ms));
    },
  });

  return (
    <div className="flex items-center gap-2">
      {discoveredBy && (
        <StatusChip variant="info" label={`Discovered by ${discoveredBy}`} />
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={!webpageKey || runRecon.isPending}
        onClick={() => runRecon.mutate()}
      >
        {runRecon.isPending ? 'Running…' : 'Run recon'}
      </Button>
    </div>
  );
}

function Card({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
      <div className="border-chariot-border flex items-center justify-between border-b px-4 py-3">
        <h4 className="text-chariot-text-primary text-sm font-medium">
          {title}
        </h4>
        {typeof count === 'number' && (
          <span className="text-chariot-text-tertiary text-xs">{count}</span>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="text-chariot-text-secondary text-sm">{label}</span>
      <span className="text-chariot-text-primary text-right text-sm font-medium break-all">
        {value}
      </span>
    </div>
  );
}

function toolFlags(tool: McpTool): string {
  const a = tool.annotations;
  if (!a) return '';
  const flags: string[] = [];
  if (a.destructive) flags.push('destructive');
  if (a.read_only) flags.push('read-only');
  if (a.open_world) flags.push('open-world');
  if (a.idempotent) flags.push('idempotent');
  return flags.join(', ');
}

/**
 * ReconErrorBanner warns the analyst when augustus recorded a reconnaissance
 * failure (a recon module that failed to construct or errored mid-run). Without
 * it a degraded/failed recon reads identically to "never run" — the silent-
 * failure gap this surfaces.
 */
function ReconErrorBanner({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <div className="border-chariot-status-warning bg-chariot-status-warning/10 rounded-lg border border-l-4 px-4 py-3">
      <div className="text-chariot-text-secondary mb-1 text-xs font-semibold tracking-wide uppercase">
        {errors.length === 1
          ? 'Reconnaissance warning'
          : 'Reconnaissance warnings'}
      </div>
      <ul className="space-y-1">
        {errors.map((e, i) => (
          <li
            key={i}
            className="text-chariot-text-primary font-mono text-xs break-all"
          >
            {e}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * LLMReconSection renders the augustus MCP reconnaissance observation for an
 * asset: a server-identity summary plus the full enumerated tool / resource /
 * prompt catalog. Data comes from the asset's `mcp-*` attributes via useLLMRecon.
 */
export function LLMReconSection({ asset }: { asset: Asset }) {
  const { recon, isFetching, targetWebpageKey, reconErrors } = useLLMRecon(
    asset.key
  );

  if (isFetching) {
    return (
      <div className="text-chariot-text-secondary p-8 text-center text-sm">
        Loading LLM reconnaissance…
      </div>
    );
  }

  if (!recon.present) {
    return (
      <div className="space-y-4">
        <ReconErrorBanner errors={reconErrors} />
        <div className="border-chariot-border bg-chariot-layer0 flex flex-col items-center justify-center gap-4 rounded-lg border p-10 text-center">
          <div className="space-y-1">
            <h4 className="text-chariot-text-primary text-sm font-medium">
              No LLM reconnaissance yet
            </h4>
            <p className="text-chariot-text-secondary mx-auto max-w-sm text-sm">
              Run reconnaissance to enumerate this MCP server&apos;s tools,
              resources, and prompts.
            </p>
          </div>
          <RunReconButton webpageKey={targetWebpageKey} />
        </div>
      </div>
    );
  }

  const { summary, inventory, observations } = recon;
  const tools = inventory?.tools ?? [];
  const resources = [
    ...(inventory?.resources ?? []),
    ...(inventory?.resource_templates ?? []),
  ];
  const prompts = inventory?.prompts ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <RunReconButton
          webpageKey={targetWebpageKey}
          discoveredBy={recon.discoveredBy}
        />
      </div>

      <ReconErrorBanner errors={reconErrors} />

      <Card title="MCP Server">
        <Row label="Server" value={summary.serverName} />
        <Row label="Version" value={summary.serverVersion} />
        <Row label="Transport" value={summary.transport} />
        <Row label="Protocol" value={summary.protocolVersion} />
        <Row label="Instructions" value={summary.instructions} />
        <div className="text-chariot-text-tertiary mt-2 text-xs">
          {summary.tools} tools · {summary.resources} resources ·{' '}
          {summary.prompts} prompts
        </div>
      </Card>

      <Card title="Tools" count={tools.length}>
        {tools.length === 0 ? (
          <div className="text-chariot-text-tertiary text-sm">None</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-chariot-text-tertiary text-xs uppercase">
                <tr>
                  <th className="py-1 pr-4">Name</th>
                  <th className="py-1 pr-4">Description</th>
                  <th className="py-1">Flags</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t: McpTool) => (
                  <tr key={t.name} className="border-chariot-border border-t">
                    <td className="text-chariot-text-primary py-1 pr-4 font-mono">
                      {t.name}
                    </td>
                    <td className="text-chariot-text-secondary py-1 pr-4">
                      {t.description || t.title || '—'}
                    </td>
                    <td className="text-chariot-text-secondary py-1">
                      {toolFlags(t) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Resources" count={resources.length}>
        {resources.length === 0 ? (
          <div className="text-chariot-text-tertiary text-sm">None</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-chariot-text-tertiary text-xs uppercase">
                <tr>
                  <th className="py-1 pr-4">URI</th>
                  <th className="py-1 pr-4">Name</th>
                  <th className="py-1">Type</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((r: McpResource, i: number) => (
                  <tr
                    key={(r.uri || r.uri_template || r.name || '') + i}
                    className="border-chariot-border border-t"
                  >
                    <td className="text-chariot-text-primary py-1 pr-4 font-mono break-all">
                      {r.uri || r.uri_template || '—'}
                    </td>
                    <td className="text-chariot-text-secondary py-1 pr-4">
                      {r.name || '—'}
                    </td>
                    <td className="text-chariot-text-secondary py-1">
                      {r.mime_type || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Prompts" count={prompts.length}>
        {prompts.length === 0 ? (
          <div className="text-chariot-text-tertiary text-sm">None</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-chariot-text-tertiary text-xs uppercase">
                <tr>
                  <th className="py-1 pr-4">Name</th>
                  <th className="py-1 pr-4">Description</th>
                  <th className="py-1">Arguments</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((p: McpPrompt) => (
                  <tr key={p.name} className="border-chariot-border border-t">
                    <td className="text-chariot-text-primary py-1 pr-4 font-mono">
                      {p.name}
                    </td>
                    <td className="text-chariot-text-secondary py-1 pr-4">
                      {p.description || p.title || '—'}
                    </td>
                    <td className="text-chariot-text-secondary py-1">
                      {(p.arguments ?? []).map(x => x.name).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Every other reconnaissance observation (identifiers, and any future
          recon module), rendered dynamically off its envelope. mcp.inventory is
          excluded — it has the dedicated cards above. */}
      <ReconObservations
        observations={observations}
        exclude={['mcp.inventory']}
      />
    </div>
  );
}
